import instance, { isErrorHandled } from 'api';
import Button from 'components/button/Button';
import { TFAField } from 'components/form/TFAField';
import { Modal } from 'components/modal/Modal';
import { endpoints } from 'endpoints.config';
import { getErrorMessage } from 'errors';
import { Form, Formik, FormikHelpers } from 'formik';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import { Toast } from 'helpers/toast';
import { useTFAField } from 'helpers/useTFAField';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { signOut } from 'reducers/auth';
import { closeModal } from 'reducers/modal';
import * as Yup from 'yup';

const initialValues = { tfaCode: '' };
const getValidationSchema = (t: any) =>
    Yup.object({
        tfaCode: Yup.string().required(t('enterTfaCode')),
    });

export const DeactivateAccountModal = () => {
    const { t } = useTranslation('security');
    const [tfaType, toggleTfaType, reSendTfaCode] = useTFAField();
    const dispatch = useDispatch();
    const [isConfirming, setConfirming] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = (
        values: typeof initialValues,
        helpers: FormikHelpers<typeof initialValues>
    ) => {
        if (!isConfirming) {
            setConfirming(true);
            helpers.setSubmitting(false);
            return;
        }
        instance
            .post(endpoints.profilemodule.disableAccount, { ...values, tfaType })
            .then((res) => {
                Toast.openSuccessToast(t('accountDisabled'));
                dispatch(closeModal());
                dispatch(signOut());
            })
            .catch((err) => {
                setConfirming(false);
                if (isErrorHandled(err)) {
                    helpers.setSubmitting(false);
                    const fieldErrors = err.response.data.errors.filter(
                        (error) => toCamelCase(error.fieldName) === 'tfaCode'
                    );
                    if (fieldErrors.length > 0) {
                        helpers.setFieldError(
                            'tfaCode',
                            getErrorMessage(fieldErrors[0].messageCode)
                        );
                    } else if (
                        err.response.data.errors.length > 0 &&
                        err.response.data.errors.some(
                            (err: any) => err.errorType === 'FundsInAccount'
                        )
                    ) {
                        const error = err.response.data.errors.find(
                            (err: any) => err.errorType === 'FundsInAccount'
                        )!;

                        if (error.messageCode === 'Invalid') {
                            helpers.setFieldError(
                                'tfaCode',
                                t('unableToDeactivate')
                            );
                        } else {
                            Toast.openGenericErrorToast();
                        }
                    } else {
                        setErrorMessage(getErrorMessage(err.response.data.errors[0].messageCode));
                    }
                } else {
                    Toast.openGenericErrorToast();
                    dispatch(closeModal());
                }
            });
    };
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={getValidationSchema(t)}
            onSubmit={handleSubmit}
        >
            {(formikProps) => (
                <Form>
                    <Modal
                        title={t('deactivateAccountTitle')}
                        customButtons={
                            !isConfirming ? (
                                <div className="ModalNavigation">
                                    <Button
                                        priority="secondary"
                                        type="button"
                                        onClick={() => {
                                            dispatch(closeModal());
                                        }}
                                    >
                                        {t('cancel', { ns: 'common' })}
                                    </Button>
                                    <Button
                                        priority="primary"
                                        type="submit"
                                        disabled={formikProps.isSubmitting || !formikProps.isValid}
                                    >
                                        {t('update', { ns: 'common' })}
                                    </Button>
                                </div>
                            ) : (
                                <div className="ModalNavigation">
                                    <Button
                                        priority="secondary"
                                        type="submit"
                                        disabled={formikProps.isSubmitting || !formikProps.isValid}
                                    >
                                        {t('update', { ns: 'common' })}
                                    </Button>
                                    <Button
                                        priority="primary"
                                        type="button"
                                        onClick={() => {
                                            dispatch(closeModal());
                                        }}
                                    >
                                        {t('cancel', { ns: 'common' })}
                                    </Button>
                                </div>
                            )
                        }
                    >
                        {!isConfirming ? (
                            <TFAField
                                field="tfaCode"
                                label={t('tfaCode')}
                                required={true}
                                toggleTFAType={toggleTfaType}
                                tfaType={tfaType}
                                handleResendCode={reSendTfaCode}
                                autoFocus
                                holdFocus
                            />
                        ) : (
                            <div className="notranslate" translate="no">
                                <h3>{t('deactivateAccount')}</h3>
                                <p>{t('deactivateConfirm')}</p>
                                <p>{t('deactivateReactivate')}</p>
                            </div>
                        )}
                    </Modal>
                </Form>
            )}
        </Formik>
    );
};
